import * as React from 'react'
import type { User } from 'oidc-client-ts'
import { getBrowserSession, userRoles, type BrowserSession } from '../lib/browserSession'
import { attachSession } from '../api/client'
import { shouldBypassAuth, DEV_USER } from '../lib/devAuth'

type AuthContextValue = {
  session: BrowserSession | null
  user: User | null
  authenticated: boolean
  roles: string[]
  login: (redirectUri?: string) => void
  logout: () => void
  initialized: boolean
  error: string | null
}
const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<BrowserSession | null>(null)
  const [user, setUser] = React.useState<User | null>(null)
  const [initialized, setInitialized] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const bypass = shouldBypassAuth()

  React.useEffect(() => {
    if (bypass) { setInitialized(true); return }
    let active = true
    let current: BrowserSession
    try { current = getBrowserSession() }
    catch { setError('Secure sign-in is unavailable in this browser. Use HTTPS in a supported browser.'); setInitialized(true); return }
    setSession(current)
    attachSession(current)
    const update = (next: User | null) => { if (active) setUser(next) }
    const unloaded = () => update(null)
    const expired = () => { void current.refresh().then(ok => { if (!ok) update(null) }) }
    const storage = (event: StorageEvent) => {
      if (event.key?.startsWith('bdi.oidc.')) void current.manager.getUser().then(update)
    }
    current.manager.events.addUserLoaded(update)
    current.manager.events.addUserUnloaded(unloaded)
    current.manager.events.addAccessTokenExpired(expired)
    window.addEventListener('storage', storage)
    void current.initialize().then(update).catch(() => {
      if (active) { setUser(null); setError('Sign-in could not be completed. Please sign in again.') }
    }).finally(() => { if (active) setInitialized(true) })
    return () => {
      active = false
      current.manager.events.removeUserLoaded(update)
      current.manager.events.removeUserUnloaded(unloaded)
      current.manager.events.removeAccessTokenExpired(expired)
      window.removeEventListener('storage', storage)
    }
  }, [bypass])

  const value: AuthContextValue = {
    session, user, initialized, error,
    authenticated: bypass || Boolean(user && !user.expired && user.token_type.toLowerCase() === 'dpop'),
    roles: bypass ? DEV_USER.roles : userRoles(user),
    login: redirectUri => { void session?.login({ redirectUri }).catch(() => setError('Sign-in could not be started. Please try again.')) },
    logout: () => {
      void session?.logout().catch(() => setError('Sign-out could not be completed. Please try again.'))
    },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
